import React from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty, map, size } from "lodash";
import { createErrorSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { getUser } from "actions/adminActions";
import { formatCurrency } from "utils/number";
import { FiUsers } from "react-icons/fi";
import { Card } from "react-bootstrap";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
import Alert from "components/Alert";
import Button from "components/Button";
import LiquidationForm from "pages/dashboards/adminDashboard/users/LiquidationForm";
import { ROTH_IRA, TRADITIONAL_IRA } from "statics/accountTypes";
import type { RootState } from "types/state";

// A raw account from the getUserDetails payload, as read inside
// `_getUserAccountsData`. Only the fields actually accessed are typed;
// `balances.*` is read via lodash `get`, so it stays loosely typed.
interface RawAccount {
  id: string | null;
  accountType: string | null;
  accountNumber: string | null;
  name: string | null;
  createdAt: string | null;
}

// A single account row as produced by `_getUserAccountsData`.
interface AccountRow {
  id: string | null;
  accountType: string | null;
  accountNumber: string | null;
  accountName: string | null;
  cashBalance: number;
  cashBalanceFormatted: string;
  withdrawalBalance: number;
  withdrawalBalanceFormatted: string;
  assetBalance: number;
  assetBalanceFormatted: string;
  pendingContributions: string;
  createdAt: string;
  isIra: boolean;
}

// LiquidationForm is TS and types account as { id?: string; assetBalance? }.
// This page passes an AccountRow whose id is string | null, so cast the
// component to the AccountRow-shaped prop accepted here; the value is unchanged.
interface LiquidationFormProps {
  onCancel: () => void;
  account?: AccountRow | Record<string, never>;
  onSuccess: () => void;
  userId?: string;
}
const LiquidationFormTyped =
  LiquidationForm as React.ComponentType<LiquidationFormProps>;

const errorSelector = createErrorSelector(adminConstants.LIQUIDATE_ACCOUNT);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getUser,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// userId is passed in by the parent (the user-detail page), not from redux.
interface OwnProps {
  userId?: string;
}

type Props = WithApolloClient<PropsFromRedux & OwnProps>;

interface State {
  // `user` starts as {} and is replaced by the getUserDetails payload.
  user: unknown;
  isFetching: boolean;
  showLiquidationForm: boolean;
  // set when the Liquidate button is clicked; absent until then.
  accountForLiquidation?: AccountRow | Record<string, never>;
}

class EmployerLinks extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      user: {},
      isFetching: true,
      showLiquidationForm: false,
    };
  }

  async componentDidMount() {
    await this._getAndSetUser();
    this.setState({
      isFetching: false,
    });
  }

  _getAndSetUser = async () => {
    // getUser resolves to the dispatched action; the SUCCESS branch carries
    // the getUserDetails payload under `data`.
    const { data: user } = (await this.props.getUser(
      this.props.client as ApolloClient<object>,
      // OwnProps.userId is string | undefined; getUser (actions/, not owned)
      // types userId as string | number. Original JS passed it through
      // unguarded — cast at the boundary so the value is unchanged.
      this.props.userId as string | number
    )) as { data?: unknown };
    this.setState({ user });
  };

  _getUserAccountsData = (): AccountRow[] => {
    const accounts = get(this.state.user, "accounts", []) as RawAccount[];
    return map(accounts, (account) => {
      const cashBalance = get(account, "balances.cashBalance", 0);
      const withdrawalBalance = get(account, "balances.withdrawalBalance", 0);
      const assetBalance = get(
        account,
        "balances.totalAccountInvestmentBalance",
        0
      );
      const pendingContributions = get(
        account,
        "balances.totalPendingContributions",
        0
      );
      const isIra =
        account.accountType === TRADITIONAL_IRA ||
        account.accountType === ROTH_IRA;

      return {
        id: account.id,
        accountType: account.accountType,
        accountNumber: account.accountNumber,
        accountName: account.name,
        cashBalance,
        cashBalanceFormatted: formatCurrency(cashBalance),
        withdrawalBalance,
        withdrawalBalanceFormatted: formatCurrency(withdrawalBalance),
        assetBalance,
        assetBalanceFormatted: formatCurrency(assetBalance),
        pendingContributions: formatCurrency(pendingContributions),
        createdAt: account.createdAt
          ? moment(account.createdAt).format("MMM D, YYYY")
          : "",
        isIra,
      };
    });
  };

  _buildUserAccountsColumns = (): Array<IconTableColumn<AccountRow>> => {
    const columns: Array<IconTableColumn<AccountRow>> = [
      {
        label: "Type",
        key: "accountType",
      },

      {
        label: "ID",
        key: "id",
      },
      {
        label: "Account Number",
        key: "accountNumber",
      },
      {
        label: "Account Name",
        key: "accountName",
      },

      {
        label: "Asset Balance",
        key: "assetBalanceFormatted",
      },
      {
        label: "Cash Balance",
        key: "cashBalanceFormatted",
      },
      {
        label: "Withdrawal Balance",
        key: "withdrawalBalanceFormatted",
      },
      {
        label: "Pending Contributions",
        key: "pendingContributions",
      },
      {
        label: "Created At",
        key: "createdAt",
      },
      {
        customComponent: (props: AccountRow & Record<string, unknown>) => {
          if (!props.isIra) return null;
          return (
            <div>
              <Button
                color="action"
                name="action"
                btnLabel={"Liquidate"}
                onClick={() =>
                  this.setState({
                    showLiquidationForm: true,
                    accountForLiquidation: props,
                  })
                }
                size={"sm"}
              />
            </div>
          );
        },
      },
    ];
    return columns;
  };

  render() {
    const columns = this._buildUserAccountsColumns();
    const data = this._getUserAccountsData();

    const userAccounts = get(this.state.user, "accounts", []);
    const userAccountsCount = size(userAccounts);

    const showBlankSlate = isEmpty(userAccounts);

    if (this.state.isFetching) {
      return <IconSpinner centered />;
    } else if (showBlankSlate) {
      return (
        <div>
          <IconMessageCard
            header={"User Accounts"}
            subheader="No user accounts exist for this employer."
            icon={<FiUsers color="white" stroke="#60A4BF" size={16} />}
          />
        </div>
      );
    }

    return (
      <Card.Body id={"admin-user-accounts"}>
        {this.props.error && <Alert type={"error"} msg={this.props.error} />}
        {!this.state.showLiquidationForm && (
          <>
            <IconTableHeader
              tableHeader="Accounts"
              tableCount={userAccountsCount}
            />
            <IconTable
              columns={columns}
              data={data}
              // found-not-fixed: `hasTruncatedRows` is not an IconTable prop
              // (not in IconTableProps, never read/spread by the component) —
              // a dead, silently-ignored prop preserved from the original JS.
              // @ts-expect-error preserve pre-existing dead prop verbatim
              hasTruncatedRows
            />{" "}
          </>
        )}
        {this.state.showLiquidationForm && (
          <LiquidationFormTyped
            onCancel={() =>
              this.setState({
                showLiquidationForm: false,
                accountForLiquidation: {},
              })
            }
            account={this.state.accountForLiquidation}
            onSuccess={() => {
              this.setState({
                showLiquidationForm: false,
                accountForLiquidation: {},
              });
            }}
            userId={this.props.userId}
          />
        )}
      </Card.Body>
    );
  }
}

export default connector(withApollo<Props>(EmployerLinks));
